import { Close } from "@mui/icons-material";
import { TooltipProps } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { COLORS } from "../../../styles/constants.style";
import { MuiBox } from "../../box/MuiBox";
import { MuiTypography } from "../../typography/MuiTypography";
import { MuiStyledTooltip } from "../MuiToolTip.style";
import { MuiViewMorePopOverWrapper } from "./ViewMorePopOver.style";

interface MuiViewMorePopOverProps {
  message: string;
  anchorText: string;
  placement?: TooltipProps["placement"];
  maxLines?: number; // 1 (single-line width-based), 2+ (multi-line height-based)
}

const defaultMinimumLinesToShowViewMore = 3;
   
export const MuiViewMorePopOver: FC<MuiViewMorePopOverProps> = ({
  message,
  anchorText,
  placement = "bottom-end",
  maxLines = defaultMinimumLinesToShowViewMore,
}) => {
  const [showToolTip, setShowToolTip] = useState(false);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  const [anchorTextWidth, setAnchorTextWidth] = useState(0);
  const ellipsisRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ellipsisRef.current;
    if (!el) return;
    const checkEllipsis = () => {
      // For single-line: check horizontal overflow (width)
      // For multi-line: check vertical overflow (height)
      // Add threshold to account for sub-pixel rendering differences across platforms
      const threshold = 2;
      const hasOverflow =
        maxLines === 1
          ? el.scrollWidth > el.clientWidth + threshold
          : el.scrollHeight > el.clientHeight + threshold;
      setIsEllipsisActive(hasOverflow);
    };
    const resizeObserver = new ResizeObserver(checkEllipsis);
    resizeObserver.observe(el);
    checkEllipsis();
    return () => {
      resizeObserver.disconnect();
    };
  }, [message, maxLines]);

  useEffect(() => {
    // Calculate anchor text width dynamically
    if (anchorRef.current) {
      const width = anchorRef.current.offsetWidth;
      setAnchorTextWidth(width);
    }
  }, [anchorText, isEllipsisActive]);

  const handleToolTipClose = () => {
    if (showToolTip) {
      setShowToolTip(false);
    }
  };

  const toggleToolTip = () => {
    setShowToolTip(true);
  };

  return (
    <MuiViewMorePopOverWrapper
      maxLines={maxLines}
      anchorTextWidth={anchorTextWidth}
    >
      <MuiBox className="ellipsis-tooltip-container">
        <MuiBox className="ellipsis" ref={ellipsisRef}>
          {message}
        </MuiBox>
        {isEllipsisActive && (
          <MuiStyledTooltip
            open={showToolTip}
            toolTipBgColor={COLORS.cirrus_black1}
            textColor={COLORS.cirrus_black}
            arrowBgColor={COLORS.cirrus_black6}
            borderWidth={"1px"}
            maxWidth="650px"
            placement={placement}
            tabIndex={0}
            title={
              <MuiBox className="tooltip-content">
                <Close onClick={handleToolTipClose} />
                <MuiTypography variant={"h3"}>{message}</MuiTypography>
              </MuiBox>
            }
          >
            <MuiBox
              className="anchor-text"
              onClick={toggleToolTip}
              ref={anchorRef}
            >
              {anchorText}
            </MuiBox>
          </MuiStyledTooltip>
        )}
      </MuiBox>
    </MuiViewMorePopOverWrapper>
  );
};
